import { useEffect, useRef, useState } from "react";
import { sensitive } from "../lib/sensitive";

import { api, ApiError } from "../api";
import { t } from "../i18n";
import type { Datapoint, Locale, OnboardingState, PlaceSuggestion } from "../types";

interface Props {
  recordId: string;
  datapoint: Datapoint;
  locale: Locale;
  busy: boolean;
  onSelected: (state: OnboardingState) => void;
  onManual: (value: string) => void;
}

/**
 * Podpowiedzi adresów idą przez nasz backend (klucz Google nie trafia do przeglądarki).
 * Gdy dostawca milczy, pole zamienia się w zwykły input — proces idzie dalej.
 */
export function AddressInput({ recordId, datapoint, locale, busy, onSelected, onManual }: Props) {
  const [query, setQuery] = useState("");
  const [items, setItems] = useState<PlaceSuggestion[]>([]);
  const [manual, setManual] = useState(false);
  const [flat, setFlat] = useState("");
  // Wybrana podpowiedź czeka na potwierdzenie — bez tego kandydat nie zdążał
  // dopisać numeru mieszkania, bo klik w podpowiedź od razu przenosił dalej.
  const [chosen, setChosen] = useState<string | null>(null);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const timer = useRef<number | null>(null);

  useEffect(() => {
    if (manual || chosen || query.trim().length < 2) {
      setItems([]);
      return;
    }
    if (timer.current) window.clearTimeout(timer.current);
    timer.current = window.setTimeout(async () => {
      try {
        const found = await api.places(recordId, query.trim());
        setItems(found.items);
        setError(null);
      } catch (caught) {
        setItems([]);
        setManual(true); // cicha awaria podpowiedzi nie może zablokować pola
        setError((caught as ApiError).message);
      }
    }, 250);
    return () => {
      if (timer.current) window.clearTimeout(timer.current);
    };
  }, [query, manual, chosen, recordId]);

  // Klik w podpowiedź tylko zaznacza adres — wysyłka dopiero po „Dalej", żeby
  // zdążyć wpisać numer mieszkania w osobnym polu.
  const pick = (item: PlaceSuggestion) => {
    setQuery(item.description);
    setChosen(item.id);
    setItems([]);
    setError(null);
  };

  const onType = (value: string) => {
    setQuery(value);
    if (chosen) setChosen(null); // zmiana adresu unieważnia wcześniejszy wybór
  };

  const submit = async () => {
    const trimmed = flat.trim();
    if (!chosen) {
      // Adres wpisany ręcznie (dostawca podpowiedzi milczy) — zapis bez geokodowania.
      onManual(query.trim());
      return;
    }
    setSending(true);
    setError(null);
    try {
      let state = await api.selectPlace(recordId, datapoint.key, chosen, locale);
      // Pola wypełnione geokodowaniem podaje backend (`autofilled`) — front już
      // ich nie zgaduje zaszytą listą.
      const autofilled = state.autofilled ?? [];
      // Numer lokalu wpisany ręcznie ma pierwszeństwo nad tym z autocomplete.
      if (trimmed) {
        state = await api.answer(recordId, "adres_nr_lokalu", trimmed, locale);
        state.autofilled = autofilled;
      }
      onSelected(state);
    } catch (caught) {
      setError((caught as ApiError).message);
    } finally {
      setSending(false);
    }
  };

  const working = busy || sending;

  return (
    <>
      <input
        className="input"
        type="text"
        autoComplete="street-address"
        placeholder={datapoint.placeholder ?? "np. Szamarzewskiego 36"}
        aria-label={datapoint.title}
        {...sensitive(datapoint)}
        value={query}
        onChange={(event) => onType(event.target.value)}
      />

      {!manual && !chosen && items.length > 0 && (
        <div className="list">
          {items.map((item) => (
            <button key={item.id} type="button" className="list__item" onClick={() => pick(item)}>
              {item.description}
            </button>
          ))}
        </div>
      )}

      {!manual && !chosen && query.trim().length >= 2 && items.length === 0 && (
        <div className="list">
          <div className="list__empty">{t("noResults", locale)}</div>
        </div>
      )}

      {chosen && <p className="field__hint">{t("addressChosen", locale)}</p>}

      <input
        className="input"
        style={{ marginTop: 10 }}
        type="text"
        inputMode="numeric"
        autoComplete="address-line2"
        placeholder={t("flatNumber", locale)}
        aria-label={t("flatNumber", locale)}
        value={flat}
        onChange={(event) => setFlat(event.target.value)}
      />
      <p className="field__hint">{t("flatNumberHint", locale)}</p>

      {datapoint.hint && <p className="field__hint">{datapoint.hint}</p>}
      {error && <p className="error">{error}</p>}

      {(manual || chosen || query.trim().length >= 2) && (
        <div className="actions">
          <button
            className={`btn btn--primary${working ? " puls" : ""}`}
            disabled={working || !query.trim()}
            onClick={submit}
          >
            {working ? t("saving", locale) : t("save", locale)}
          </button>
        </div>
      )}
    </>
  );
}
